import { buildGuideResponse, eventFromChannelName, NAME_MATCH_VERSION, WINDOW_BACK_HOURS, WINDOW_FWD_HOURS } from "@hybrid-iptv/epg-parser";
import type { GuideFeed, GuideRequest, GuideResponse, NamedEvent, XMLTVChannelEntry } from "@hybrid-iptv/epg-parser";
import { hashId, XtreamError } from "@hybrid-iptv/iptv-parser";
import type { Program } from "@hybrid-iptv/types";
import { dbGet, dbPut } from "../storage/db.js";

/**
 * The APK's guide, page side, when the native Guide plugin is there
 * (native/android/.../guide/GuidePlugin.java). Native code downloads the feed,
 * scans it as it arrives and keeps the retained programmes on disk; the page
 * holds only the channel list and asks for the programmes one request needs.
 *
 * Why (owner 2026-09-19): holding the parsed feed in the page — even in a
 * worker — froze an onn 4K box at startup, and on a 1 GB projector the feed
 * measured 45.7 MB / 151,039 programmes against ~160 MB free. The response is
 * still built by the shared buildGuideResponse, so it is the same answer, with
 * the same programme ids, as the worker path and the server give.
 */

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "Guide";

/** A guide older than the TTL but younger than this is shown at once while a fresh one downloads. */
const STALE_OK_MS = 24 * 60 * 60 * 1000;

interface SyncResult {
  /** An extra source asked without waiting, with no guide of it in hand yet: its download has started. */
  missing?: boolean;
  fetchedAt: number;
  feedChannels: number;
  programmes: number;
  kept: number;
  megabytes: number;
  parseMs: number;
  cached: boolean;
  stale: boolean;
}

/** [channelId, startMs, stopMs, title, genre, subtitle?, description?] */
type Row = [string, number, number, string, string | null, (string | null)?, (string | null)?];

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside an APK that carries the native guide. */
export function nativeGuideAvailable(): boolean {
  return capacitor() !== null;
}

/** A guide download's progress: bytes read so far, and the feed's size when the server says it (-1 otherwise). */
export interface GuideProgress {
  downloading: boolean;
  bytes: number;
  total: number;
}

/** How far the native guide download has got (GuidePlugin.progress); null outside the APK. */
export async function nativeGuideProgress(): Promise<GuideProgress | null> {
  if (!capacitor()) return null;
  return call<GuideProgress>("progress", {});
}

const ERROR_CODES = new Set(["timeout", "unreachable", "http_error", "malformed_response"]);

async function call<T>(method: string, options: Record<string, unknown>): Promise<T> {
  const cap = capacitor();
  if (!cap) throw new XtreamError("malformed_response", "native guide unavailable");
  try {
    return (await cap.nativePromise!(PLUGIN, method, options)) as T;
  } catch (error) {
    const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
    if (typeof code === "string" && ERROR_CODES.has(code)) {
      throw new XtreamError(code as XtreamError["code"], typeof message === "string" && message ? message : undefined);
    }
    throw new XtreamError("malformed_response", "guide failed");
  }
}

/**
 * The channel list of each guide in hand, kept as ONE array per feed version:
 * matchFeedChannelsByName caches its matches by the array itself, so a new
 * array per request would re-match every time. One per source (the playlist's
 * own guide and its extra sources, Phase 3 step 3), the most recent six.
 */
const heldChannels = new Map<string, { fetchedAt: number; channels: XMLTVChannelEntry[] }>();
/** Counts the lists read, so a tuned mapping that checked another source's pins is worked out again. */
let channelListsRead = 0;

async function channelsFor(url: string, fetchedAt: number): Promise<XMLTVChannelEntry[]> {
  const held = heldChannels.get(url);
  if (held?.fetchedAt === fetchedAt) return held.channels;
  const result = await call<{ fetchedAt: number; channels: Array<[string, string | null]> }>("channels", { url });
  const channels = result.channels.map(([id, displayName]) => (displayName ? { id, displayName } : { id }));
  heldChannels.delete(url);
  heldChannels.set(url, { fetchedAt: result.fetchedAt, channels });
  channelListsRead += 1;
  for (const old of heldChannels.keys()) if (heldChannels.size > 6) heldChannels.delete(old);
  return channels;
}

/** The same bounds filterProgramsByWindow uses, so the native slice holds everything it would keep. */
function windowBounds(windowHours: number | null): { fromMs: number | null; toMs: number | null } {
  if (windowHours === null) return { fromMs: null, toMs: null };
  const now = new Date();
  now.setMinutes(0, 0, 0);
  return {
    fromMs: now.getTime() - WINDOW_BACK_HOURS * 3_600_000,
    toMs: now.getTime() + (windowHours + WINDOW_FWD_HOURS) * 3_600_000,
  };
}

/**
 * Which feed channels a catalog's channel names refer to, remembered.
 *
 * WHY. The matcher scores every requested name against the feed's channels by
 * brand-token overlap, and on the projector that cost 3.7 s of the page's own
 * thread — the single largest item in an 11.8 s freeze during a cold start
 * (measured 2026-09-22). It ran on EVERY launch, because the matcher's own
 * memo lives in a closure that dies with the page.
 *
 * The answer only depends on two things, and both are in the key: the feed
 * (its url and the moment it was fetched) and the exact list of names asked
 * about. A new download or an edited channel list writes a new key and the old
 * one is simply never read again.
 *
 * NOT A CHEAPER MATCH. Whatever the matcher finds today it still finds; this
 * only stops it being computed twice. The extra matches it makes — a feed
 * channel reached through brand tokens rather than an exact name — are the
 * reason the fuzzy pass exists, and caching preserves them by construction,
 * because what is stored is its own output.
 */
async function matchedNames(
  epgUrl: string,
  fetchedAt: number,
  channels: XMLTVChannelEntry[],
  names: string[],
  match: MatchChannels,
): Promise<Record<string, string[]>> {
  // The url carries credentials, so it is hashed rather than stored. "names:"
  // holds the whole name mapping; the older "match:" entries held only the
  // ids, which left the page to match everything again for the mapping. The
  // matcher's version is in the key, so new matching rules apply at once.
  let namesKey = namesKeys.get(names);
  if (namesKey === undefined) {
    namesKey = hashId("names", [names.length, ...names].join("\n"));
    namesKeys.set(names, namesKey);
  }
  const key = `names${NAME_MATCH_VERSION}:${hashId(epgUrl, `${fetchedAt}`)}:${namesKey}`;
  // Held in memory too: the guide now asks once per category shown (Phase 3
  // step 2a), and reading the whole mapping back from IndexedDB each time
  // would cost more than the programmes themselves.
  const held = heldMappings.get(key);
  if (held) {
    holdMapping(key, held);
    return held;
  }
  try {
    const known = await dbGet<Record<string, string[]>>("epgCache", key);
    if (known && typeof known === "object" && !Array.isArray(known)) {
      holdMapping(key, known);
      return known;
    }
  } catch {
    // A cache that cannot be read is not a reason to fail the guide.
  }
  const mapping = await match(
    channels.map((channel) => [channel.id, channel.displayName ?? null] as [string, string | null]),
    names,
  );
  holdMapping(key, mapping);
  try {
    await dbPut("epgCache", mapping, key);
  } catch {
    // Nor is one that cannot be written.
  }
  return mapping;
}

/**
 * The names' part of the key, per names array: the guide asks once per category
 * with the same array of every channel's name (epg/hooks.ts), and hashing ten
 * thousand names each time was a cost of its own.
 */
const namesKeys = new WeakMap<readonly string[], string>();

/**
 * The most recent mappings: the screens' channel list and the alert scan's
 * (NotificationEngine), which differs, for the playlist's own guide and each
 * extra source (Phase 3 step 3).
 */
const heldMappings = new Map<string, Record<string, string[]>>();

function holdMapping(key: string, mapping: Record<string, string[]>): void {
  heldMappings.delete(key);
  heldMappings.set(key, mapping);
  for (const old of heldMappings.keys()) if (heldMappings.size > 8) heldMappings.delete(old);
}

/**
 * Extra guide sources' channel ids are kept apart from the playlist's own guide's
 * (Phase 3 step 3): two feeds often use the same ids ("CNN.us"), and the page maps
 * programmes to its channels by feed channel id. The own guide's ids, and so its
 * programmes' ids that reminders and recordings hold, are unchanged.
 */
const EXTRA_FEED_MARK = "xfeed~";

export function extraFeedPrefix(url: string): string {
  return `${EXTRA_FEED_MARK}${hashId("feed", url).slice(0, 8)}~`;
}

/** A feed channel id from an extra guide source (the DVR's series rules use the own guide only). */
export function isExtraFeedChannel(feedChannelId: string): boolean {
  return feedChannelId.startsWith(EXTRA_FEED_MARK);
}

/**
 * The feed channels backing any of the given catalog names. The mapping holds
 * the names as the page sent them, lower-cased and trimmed (epg/hooks.ts).
 */
export function feedIdsFor(mapping: Record<string, string[]>, onlyNames: readonly string[]): string[] {
  const only = new Set(onlyNames.map((name) => name.toLowerCase().trim()));
  return Object.keys(mapping).filter((feedId) => mapping[feedId]!.some((name) => only.has(name.toLowerCase().trim())));
}

/**
 * Runs the matcher — off the page's thread when there is a worker to run it
 * on. Answers the name mapping: feed channel id → the requested names it backs.
 */
export type MatchChannels = (channels: Array<[string, string | null]>, names: string[]) => Promise<Record<string, string[]>>;

/** What one screen asks of the native guide beyond the request itself (Phase 3 step 2). */
export interface NativeGuideAsk {
  /**
   * The channels one screen shows (2a). The name mapping is still made over
   * every channel (the same match, from the same cache), but programmes come
   * back for these channels only, so a screen never takes the whole feed. The
   * mapping and the feed's channel list come back whole: the DVR sync needs
   * them for every channel.
   */
  onlyNames?: readonly string[] | null;
  /** Exact times instead of the request's window (the alert scan); windowHours should then be null. */
  bounds?: { fromMs: number; toMs: number } | null;
  /** Programmes starting within the bounds, rather than overlapping them (the alert scan). */
  startsOnly?: boolean;
}

/**
 * The viewer's fixes to a playlist's guide (Phase 3 step 4), with every source
 * named by its address. Channels are named as the guide matches them
 * (lower-cased, trimmed).
 */
export interface NativeGuideTuning {
  /** "Choose guide": the source and guide channel a channel's programmes come from. */
  pins: Readonly<Record<string, { url: string; guideChannelId: string }>>;
  /** Minutes per channel: the viewer's own, else the playlist's tvg-shift. */
  channelShifts: Readonly<Record<string, number>>;
  /** Minutes per source. */
  feedShifts: Readonly<Record<string, number>>;
}

export const NO_TUNING: NativeGuideTuning = { pins: {}, channelShifts: {}, feedShifts: {} };

/**
 * The same fixes as the page keeps them (epg/guideFeeds.ts guideTuning), before
 * the TV has chosen the playlist's own guide: a pin's `url` is null for it.
 */
export interface GuideTuning {
  pins: Readonly<Record<string, { url: string | null; guideChannelId: string }>>;
  /** The viewer's own, per channel. */
  shifts: Readonly<Record<string, number>>;
  ownShift: number;
  /** Per extra source, by address. */
  feedShifts: Readonly<Record<string, number>>;
}

const resolvedTunings = new WeakMap<GuideTuning, { own: string | null; epgUrl: string; playlist: object | null; tuning: NativeGuideTuning }>();

/**
 * The fixes with every source named by address (deviceApi's guide). `own` is the
 * playlist's own guide, null when it has none (an M3U then takes its first extra
 * source as its guide, `epgUrl`). `playlistShifts`: the playlist's tvg-shift per
 * channel, below the viewer's own. The same object while nothing changes, so the
 * tuned mappings are worked out once.
 */
export function resolveTuning(
  tuning: GuideTuning,
  own: string | null,
  epgUrl: string,
  playlistShifts: Readonly<Record<string, number>> | null,
): NativeGuideTuning {
  const held = resolvedTunings.get(tuning);
  if (held && held.own === own && held.epgUrl === epgUrl && held.playlist === playlistShifts) return held.tuning;
  const pins: Record<string, { url: string; guideChannelId: string }> = {};
  for (const [name, pin] of Object.entries(tuning.pins)) {
    const url = pin.url ?? own;
    if (url) pins[name] = { url, guideChannelId: pin.guideChannelId };
  }
  const feedShifts: Record<string, number> = { ...tuning.feedShifts };
  if (own && tuning.ownShift) feedShifts[own] = tuning.ownShift;
  const channelShifts = playlistShifts ? { ...playlistShifts, ...tuning.shifts } : tuning.shifts;
  const resolved: NativeGuideTuning = { pins, channelShifts, feedShifts };
  resolvedTunings.set(tuning, { own, epgUrl, playlist: playlistShifts, tuning: resolved });
  return resolved;
}

/**
 * A guide channel that some of its channels show at another time than the rest
 * ("Channel 4 +1" matched to Channel 4's listing) is given a second id per
 * shift; the programmes are read once and given to each.
 */
const SHIFT_MARK = "~shift~";

function shiftedId(feedChannelId: string, minutes: number): string {
  return `${feedChannelId}${SHIFT_MARK}${minutes}`;
}

/** The guide channel an id stands for: itself, or the one a shifted id was made from. */
export function baseFeedChannelId(feedChannelId: string): string {
  const at = feedChannelId.lastIndexOf(SHIFT_MARK);
  return at < 0 ? feedChannelId : feedChannelId.slice(0, at);
}

/** The minutes an answer's channel id is shown at: a shifted id's own, else its source's. */
export function feedChannelShift(feedChannelId: string, sourceShift: number): number {
  const at = feedChannelId.lastIndexOf(SHIFT_MARK);
  return at < 0 ? sourceShift : Number(feedChannelId.slice(at + SHIFT_MARK.length)) || 0;
}

const has = (record: object, key: string) => Object.prototype.hasOwnProperty.call(record, key);

/** One source's name mapping with the viewer's fixes applied. */
export interface TunedMapping {
  mapping: Record<string, string[]>;
  /** Guide channel -> the ids its programmes are given and each one's shift; null when every one is its own, at the source's shift. */
  outs: Map<string, Array<[string, number]>> | null;
  feedShift: number;
  minShift: number;
  maxShift: number;
}

const tunedMappings = new WeakMap<Record<string, string[]>, { tuning: NativeGuideTuning; url: string; read: number; tuned: TunedMapping }>();

const listedIds = new WeakMap<readonly XMLTVChannelEntry[], Set<string>>();

function idsOf(channels: readonly XMLTVChannelEntry[]): Set<string> {
  let ids = listedIds.get(channels);
  if (!ids) {
    ids = new Set(channels.map((channel) => channel.id));
    listedIds.set(channels, ids);
  }
  return ids;
}

/**
 * Whether a pin is used: its source must be one the playlist reads (guideTuning
 * drops the rest) and, once its channel list is in hand, still list the guide
 * channel. A guide channel gone from its source gives the channel back to its
 * automatic match rather than leaving it empty.
 */
function pinHolds(pin: { url: string; guideChannelId: string }): boolean {
  const listed = heldChannels.get(pin.url)?.channels;
  return !listed || idsOf(listed).has(pin.guideChannelId);
}

/**
 * The mapping as the viewer fixed it (Phase 3 step 4). A pinned channel is
 * taken out of every source's automatic match and given to its pinned guide
 * channel, when that source lists it; a channel with a shift of its own goes
 * under a shifted id. Worked out once per mapping (the cached matcher's own
 * object) and tuning; without either it is the mapping itself.
 */
export function tuneMapping(
  mapping: Record<string, string[]>,
  url: string,
  channels: readonly XMLTVChannelEntry[],
  names: readonly string[],
  tuning: NativeGuideTuning,
): TunedMapping {
  const feedShift = tuning.feedShifts[url] ?? 0;
  const pinned = Object.keys(tuning.pins);
  const shifted = Object.keys(tuning.channelShifts);
  if (pinned.length === 0 && shifted.length === 0) return { mapping, outs: null, feedShift, minShift: feedShift, maxShift: feedShift };
  const held = tunedMappings.get(mapping);
  if (held && held.tuning === tuning && held.url === url && held.read === channelListsRead) return held.tuned;
  let tuned = mapping;
  // This source's channel list is in hand by now; the others' are once read.
  const holding = new Set(pinned.filter((name) => {
    const pin = tuning.pins[name]!;
    return pin.url === url ? idsOf(channels).has(pin.guideChannelId) : pinHolds(pin);
  }));
  if (holding.size > 0) {
    tuned = {};
    for (const [id, mapped] of Object.entries(mapping)) {
      const kept = mapped.filter((name) => !holding.has(name));
      if (kept.length > 0) tuned[id] = kept;
    }
    const asked = new Set(names);
    for (const name of holding) {
      const { url: pinnedUrl, guideChannelId } = tuning.pins[name]!;
      if (pinnedUrl === url && asked.has(name)) (tuned[guideChannelId] ??= []).push(name);
    }
  }
  let outs: Map<string, Array<[string, number]>> | null = null;
  let minShift = feedShift;
  let maxShift = feedShift;
  if (shifted.length > 0) {
    const split: Record<string, string[]> = {};
    for (const [id, mapped] of Object.entries(tuned)) {
      const groups = new Map<number, string[]>();
      for (const name of mapped) {
        const shift = has(tuning.channelShifts, name) ? tuning.channelShifts[name]! : feedShift;
        const group = groups.get(shift);
        if (group) group.push(name);
        else groups.set(shift, [name]);
      }
      if (groups.size === 1 && groups.has(feedShift)) {
        split[id] = mapped;
        continue;
      }
      // The id itself stays with the source's shift (feedChannelShift reads the
      // rest from the id), so programme ids change only for a channel's own shift.
      const ids: Array<[string, number]> = [];
      for (const [shift, group] of groups) {
        const outId = shift === feedShift ? id : shiftedId(id, shift);
        split[outId] = group;
        ids.push([outId, shift]);
        minShift = Math.min(minShift, shift);
        maxShift = Math.max(maxShift, shift);
      }
      (outs ??= new Map()).set(id, ids);
    }
    tuned = split;
  }
  const result: TunedMapping = { mapping: tuned, outs, feedShift, minShift, maxShift };
  tunedMappings.set(mapping, { tuning, url, read: channelListsRead, tuned: result });
  return result;
}

/**
 * Channel names as the provider wrote them, by the key the guide matches on
 * (lower-cased, trimmed): an event read from a name keeps its capitals
 * ("NCAAW Soccer: SMU vs California", not "ncaaw soccer: smu vs california").
 * Filled from each channel list the screens ask with (epg/hooks.ts), once per list.
 */
const writtenNames = new Map<string, string>();
const namesRead = new WeakSet<readonly { name: string }[]>();

export function rememberChannelNames(channels: readonly { name: string }[]): void {
  if (namesRead.has(channels)) return;
  namesRead.add(channels);
  if (writtenNames.size > 50_000) writtenNames.clear();
  for (const channel of channels) {
    const key = channel.name.toLowerCase().trim();
    if (key) writtenNames.set(key, channel.name.trim());
  }
}

/**
 * Events read from names, by the name as written: a name is read once, not on
 * every request, and one read before its written form was known is read again.
 */
const namedEvents = new Map<string, NamedEvent | null>();

function namedEventOf(name: string, nowMs: number): NamedEvent | null {
  const written = writtenNames.get(name) ?? name;
  let event = namedEvents.get(written);
  if (event === undefined) {
    if (namedEvents.size > 50_000) namedEvents.clear();
    event = eventFromChannelName(written, nowMs);
    namedEvents.set(written, event);
  }
  return event;
}

/** Ids of channels whose guide entry was read from their name; kept apart like an extra source's (the DVR skips them). */
const NAMED_PREFIX = "xfeed~names~";

/**
 * Event slots no guide covers get an entry read from their own name (playbook
 * Phase 3f, "guide from channel names"; @hybrid-iptv/epg-parser eventNames):
 * "NCAAW Soccer: SMU vs California @ Oct 22 3:00 PM :SEC+ 03" shows that game
 * from 3 PM. Only names no source has programmes for, so real guide data always
 * wins; only around the event's own time, so a name left over from yesterday
 * is never shown as on now.
 */
function namedEventPart(
  sourceId: string,
  request: GuideRequest,
  ask: NativeGuideAsk,
  covered: ReadonlySet<string>,
  sync: SyncResult,
): FeedPart | null {
  const names = ask.onlyNames ?? request.channelNames ?? [];
  const window = ask.bounds ?? windowBounds(request.windowHours);
  const now = Date.now();
  const mapping: Record<string, string[]> = {};
  const programs: Program[] = [];
  for (const name of names) {
    if (covered.has(name)) continue;
    const event = namedEventOf(name, now);
    if (!event) continue;
    if (window.fromMs !== null && window.toMs !== null) {
      const inside = ask.startsOnly
        ? event.startMs >= window.fromMs && event.startMs < window.toMs
        : event.endMs > window.fromMs && event.startMs < window.toMs;
      if (!inside) continue;
    }
    const channelId = NAMED_PREFIX + name;
    mapping[channelId] = [name];
    programs.push({
      id: hashId(sourceId, `${channelId}:${event.startMs}`),
      channelId,
      title: event.title,
      start: new Date(event.startMs).toISOString(),
      end: new Date(event.endMs).toISOString(),
    });
  }
  if (programs.length === 0) return null;
  return { sync, channels: [], mapping, channelIds: Object.keys(mapping), programs };
}

/** One guide source's part of an answer. Ids carry the source's prefix for an extra source. */
interface FeedPart {
  sync: SyncResult;
  channels: XMLTVChannelEntry[];
  mapping: Record<string, string[]> | null;
  channelIds: string[] | null;
  programs: Program[];
}

/**
 * One source's part: its sync, its channels, the names matched against it, and
 * the programmes asked for. `prefix` marks an extra source: it is asked without
 * waiting (null while its first download runs), and only for the names it is
 * given, which are the ones the sources before it have no programmes for.
 */
async function readFeed(
  epgUrl: string,
  sourceId: string,
  request: GuideRequest,
  names: string[] | null,
  ttlMs: number,
  match: MatchChannels,
  ask: NativeGuideAsk,
  prefix: string,
  tuning: NativeGuideTuning,
): Promise<FeedPart | null> {
  const { onlyNames = null, bounds = null, startsOnly = false } = ask;
  const sync = await call<SyncResult>("sync", {
    url: epgUrl,
    maxAgeMs: ttlMs,
    staleMs: STALE_OK_MS,
    force: request.refresh && !prefix,
    ...(prefix ? { wait: false } : {}),
  });
  if (sync.missing) return null;
  if (!sync.cached) {
    // Section 22 Phase 1's full-guide measurement, in logcat. Never the URL — it holds credentials.
    console.info(
      `[guide] ${sync.megabytes.toFixed(1)} MB downloaded and parsed natively in ${sync.parseMs} ms: ` +
        `${sync.programmes} programmes, ${sync.kept} kept, ${sync.feedChannels} channels`,
    );
  }
  const channels = await channelsFor(epgUrl, sync.fetchedAt);
  const matched = names ? await matchedNames(epgUrl, sync.fetchedAt, channels, names, match) : null;
  const tuned = matched && names ? tuneMapping(matched, epgUrl, channels, names, tuning) : null;
  const mapping = tuned ? tuned.mapping : null;
  const channelIds = mapping ? (onlyNames ? feedIdsFor(mapping, onlyNames) : Object.keys(mapping)) : request.channelIds;
  // The TV holds each guide channel once, under its own id.
  const askIds = channelIds && tuned?.outs ? [...new Set(channelIds.map(baseFeedChannelId))] : channelIds;
  const feedShift = tuned?.feedShift ?? tuning.feedShifts[epgUrl] ?? 0;
  const minShift = tuned?.minShift ?? feedShift;
  const maxShift = tuned?.maxShift ?? feedShift;
  // Programmes shown later were listed earlier: the TV is asked for the hours
  // that land in the window once moved, and anything moved out of it is left out.
  const window = bounds ?? windowBounds(request.windowHours);
  const moved = minShift !== 0 || maxShift !== 0;
  const asked =
    moved && window.fromMs !== null && window.toMs !== null
      ? { fromMs: window.fromMs - maxShift * 60_000, toMs: window.toMs - minShift * 60_000 }
      : window;
  // An extra source with none of this screen's channels: nothing to ask it.
  const rows =
    prefix && !startsOnly && askIds !== null && askIds.length === 0
      ? []
      : (
          await call<{ rows: Row[] }>("programmes", {
            url: epgUrl,
            channelIds: askIds,
            ...asked,
            details: request.includeDetails,
            ...(startsOnly ? { startsOnly: true } : {}),
          })
        ).rows;
  // Built exactly as parseXMLTV builds them, id included; the id is made from
  // the listed time, so changing a shift keeps it.
  const programs: Program[] = [];
  for (const [feedChannelId, startMs, stopMs, title, genre, subtitle, description] of rows) {
    const outs = tuned?.outs?.get(feedChannelId);
    for (const [outId, shift] of outs ?? [[feedChannelId, feedShift] as [string, number]]) {
      const channelId = prefix + outId;
      const start = startMs + shift * 60_000;
      const end = stopMs + shift * 60_000;
      if (shift !== 0 && window.fromMs !== null && window.toMs !== null) {
        const inside = startsOnly ? start >= window.fromMs && start < window.toMs : end > window.fromMs && start < window.toMs;
        if (!inside) continue;
      }
      programs.push({
        id: hashId(sourceId, `${channelId}:${startMs}`),
        channelId,
        title,
        ...(subtitle ? { subtitle } : {}),
        ...(description ? { description } : {}),
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        ...(genre ? { genre } : {}),
      });
    }
  }
  if (!prefix) return { sync, channels, mapping, channelIds, programs };
  const prefixed: Record<string, string[]> = {};
  for (const [id, mapped] of Object.entries(mapping ?? {})) prefixed[prefix + id] = mapped;
  return {
    sync,
    channels: channels.map((channel) => ({ ...channel, id: prefix + channel.id })),
    mapping: mapping ? prefixed : null,
    channelIds: channelIds ? channelIds.map((id) => prefix + id) : null,
    programs,
  };
}

/**
 * `extraUrls` (Phase 3 step 3): the playlist's switched-on extra guide sources,
 * in order. A channel takes its programmes from the first source that has it: the
 * own guide, then each extra for the names still without one. Without extras this
 * is the one guide, exactly as before.
 */
export async function answerNativeGuide(
  epgUrl: string,
  sourceId: string,
  request: GuideRequest,
  ttlMs: number,
  match: MatchChannels,
  ask: NativeGuideAsk = {},
  extraUrls: readonly string[] = [],
  tuning: NativeGuideTuning = NO_TUNING,
): Promise<GuideResponse & { guideShift?: number }> {
  const own = (await readFeed(epgUrl, sourceId, request, request.channelNames, ttlMs, match, ask, "", tuning))!;
  // The own guide's shift, for the DVR's series check (dvrSync), which follows the own guide only.
  const shiftOf = (url: string) => (tuning.feedShifts[url] ? { guideShift: tuning.feedShifts[url] } : {});
  const meta = { epgUrl, cached: own.sync.cached };
  const ownFeed = (programs: Program[], channels: XMLTVChannelEntry[]): GuideFeed => ({
    programs,
    channels,
    feedChannels: own.sync.feedChannels,
    fetchedAt: new Date(own.sync.fetchedAt).toISOString(),
  });
  if (!own.mapping || !request.channelNames) return buildGuideResponse(ownFeed(own.programs, own.channels), request, meta);
  const parts: FeedPart[] = [];
  const covered = new Set<string>();
  for (const names of Object.values(own.mapping)) for (const name of names) covered.add(name);
  for (const url of extraUrls) {
    const remaining = request.channelNames.filter((name) => !covered.has(name));
    if (remaining.length === 0) break;
    const part = await readFeed(url, sourceId, request, remaining, ttlMs, match, ask, extraFeedPrefix(url), tuning).catch(() => null);
    if (!part?.mapping) continue;
    parts.push(part);
    for (const names of Object.values(part.mapping)) for (const name of names) covered.add(name);
  }
  const named = namedEventPart(sourceId, request, ask, covered, own.sync);
  if (named) parts.push(named);
  // Built from the ids, with the mapping put back. Handed the names instead,
  // buildGuideResponse matched all ~10k of them AGAIN on the page's thread —
  // 9 s of a cold start on the projector (2026-09-26), after the worker and
  // the cache had already answered.
  if (parts.length === 0) {
    const response = buildGuideResponse(ownFeed(own.programs, own.channels), { ...request, channelNames: null, channelIds: own.channelIds }, meta);
    return { ...response, requestedChannels: request.channelNames, requestedVia: "names" as const, nameMapping: own.mapping, ...shiftOf(epgUrl) };
  }
  const feed = ownFeed(
    [own.programs, ...parts.map((part) => part.programs)].flat(),
    [own.channels, ...parts.map((part) => part.channels)].flat(),
  );
  const channelIds = [own.channelIds ?? [], ...parts.map((part) => part.channelIds ?? [])].flat();
  const response = buildGuideResponse(feed, { ...request, channelNames: null, channelIds }, meta);
  const nameMapping = Object.assign({}, own.mapping, ...parts.map((part) => part.mapping));
  return { ...response, requestedChannels: request.channelNames, requestedVia: "names" as const, nameMapping, ...shiftOf(epgUrl) };
}

/** A guide source as Settings → Playlists → Guide sources shows it (GuidePlugin.status); null outside the APK. */
export interface GuideSourceStatus {
  held: boolean;
  fetchedAt?: number;
  feedChannels?: number;
  programmes?: number;
  diskBytes: number;
  downloading: boolean;
}

export async function nativeGuideStatus(url: string): Promise<GuideSourceStatus | null> {
  if (!capacitor()) return null;
  return call<GuideSourceStatus>("status", { url });
}

/** Whether an address is a guide, from its start only (GuidePlugin.probe). */
export async function nativeGuideProbe(url: string): Promise<{ ok: boolean; reason?: "unreachable" | "http" | "not_guide"; status?: number }> {
  return call("probe", { url });
}

/** Start a source's download if it is due, without waiting for it. */
export async function nativeGuideFetch(url: string, ttlMs: number): Promise<void> {
  await call("sync", { url, maxAgeMs: ttlMs, staleMs: STALE_OK_MS, wait: false });
}

/** A guide source's channels, for "Choose guide" (Phase 3 step 4); null when the TV has none of it yet. */
export async function nativeGuideChannels(url: string): Promise<XMLTVChannelEntry[] | null> {
  const status = await call<GuideSourceStatus>("status", { url });
  if (!status.held || status.fetchedAt === undefined) return null;
  return channelsFor(url, status.fetchedAt);
}

/** What is on now on some of a source's guide channels, as listed (no shift): guide channel id → title. */
export async function nativeGuideNow(url: string, channelIds: readonly string[]): Promise<Map<string, string>> {
  const now = Date.now();
  const titles = new Map<string, string>();
  if (channelIds.length === 0) return titles;
  const { rows } = await call<{ rows: Row[] }>("programmes", { url, channelIds, fromMs: now, toMs: now + 1, details: false });
  for (const [id, startMs, stopMs, title] of rows) if (startMs <= now && stopMs > now && !titles.has(id)) titles.set(id, title);
  return titles;
}
